import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { FileUp, Search, X } from "lucide-react";
import RxUploadDetail, { STATUS_TONE } from "@/components/admin/RxUploadDetail";
import { sourceLabel } from "@/components/admin/ReportsTab";

const when = (iso) =>
  iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

/** Admin → Rx Uploads: prescriptions sent in from the public page and QR posters. */
export const RxUploadsTab = () => {
  const [data, setData] = useState({ uploads: [], counts: {}, sources: [], statuses: [] });
  const [status, setStatus] = useState("all");
  const [src, setSrc] = useState("all");
  const [query, setQuery] = useState("");
  const [term, setTerm] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [open, setOpen] = useState(null);

  // The admin types freely; the queue is re-read once the typing settles.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(query.trim()), 350);
    return () => clearTimeout(timer);
  }, [query]);

  const load = () =>
    api.get("/admin/prescription-uploads", {
      params: { status, src, q: term, from_date: from, to_date: to },
    })
      .then(({ data: body }) => setData(body))
      .catch(() => {});
  useEffect(() => { load(); }, [status, src, term, from, to]);

  const narrowed = Boolean(term || from || to || status !== "all" || src !== "all");
  const clearAll = () => { setQuery(""); setTerm(""); setFrom(""); setTo(""); setStatus("all"); setSrc("all"); };

  const filters = (options, value, setValue, prefix) => (
    <div className="flex flex-wrap gap-2">
      {["all", ...options].map((option) => (
        <Button
          key={option}
          size="sm"
          variant={value === option ? "default" : "outline"}
          className="btn-pill h-8 capitalize text-xs"
          onClick={() => setValue(option)}
          data-testid={`${prefix}-${option}`}
        >
          {option}
          {prefix === "rx-filter-status" && option !== "all" && data.counts?.[option] ? ` (${data.counts[option]})` : ""}
        </Button>
      ))}
    </div>
  );

  return (
    <Card className="border-border" data-testid="rx-uploads-tab">
      <CardContent className="p-6 lg:p-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h3 className="font-display text-2xl flex items-center gap-2"><FileUp size={18} /> Prescription uploads</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Sent in from /upload-prescription and the referral QR posters. Newest first.
            </p>
          </div>
          <Badge variant="outline" data-testid="rx-total">{data.counts?.total || 0} total</Badge>
        </div>

        <div className="space-y-3 mb-6">
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
            <div>
              <span className="overline block mb-2">Search</span>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Patient name, mobile digits, doctor name or code"
                  className="bg-transparent h-10 pl-9 pr-9"
                  data-testid="rx-search-input"
                />
                {query && (
                  <button
                    onClick={() => setQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                    data-testid="rx-search-clear"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <div>
              <span className="overline block mb-2">Received between</span>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={from}
                  max={to || undefined}
                  onChange={(e) => setFrom(e.target.value)}
                  className="bg-transparent h-10 w-[9.5rem]"
                  data-testid="rx-date-from"
                />
                <span className="text-xs text-muted-foreground">to</span>
                <Input
                  type="date"
                  value={to}
                  min={from || undefined}
                  onChange={(e) => setTo(e.target.value)}
                  className="bg-transparent h-10 w-[9.5rem]"
                  data-testid="rx-date-to"
                />
                {narrowed && (
                  <Button variant="ghost" size="sm" className="btn-pill h-9" onClick={clearAll}
                          data-testid="rx-filters-clear">
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div>
            <span className="overline block mb-2">Status</span>
            {/* "Draft" sits with the statuses because that is where the clinic looks,
                but it means something different: work started and not yet confirmed. */}
            {filters([...(data.statuses || []), "draft"], status, setStatus, "rx-filter-status")}
          </div>
          <div>
            <span className="overline block mb-2">Source / doctor code</span>
            {filters(data.sources || [], src, setSrc, "rx-filter-src")}
          </div>
        </div>

        {data.uploads.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="rx-empty">
            {narrowed ? "No matching requests found" : "No prescriptions have come in yet."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4 font-normal">Received</th>
                  <th className="py-2 pr-4 font-normal">Patient</th>
                  <th className="py-2 pr-4 font-normal">Mobile</th>
                  <th className="py-2 pr-4 font-normal">Source</th>
                  <th className="py-2 pr-4 font-normal">Status</th>
                  <th className="py-2 font-normal" />
                </tr>
              </thead>
              <tbody>
                {data.uploads.map((row) => (
                  <tr key={row.id} className="border-b border-border/60" data-testid={`rx-row-${row.id}`}>
                    <td className="py-3 pr-4 whitespace-nowrap text-muted-foreground">{when(row.created_at)}</td>
                    <td className="py-3 pr-4">{row.name}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">{row.phone}</td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge variant="outline" className="text-[10px]">{sourceLabel(row.src)}</Badge>
                        {row.ref && (
                          <Badge variant="outline" className="text-[10px] border-primary/30 text-primary bg-primary/5"
                                 data-testid={`rx-ref-${row.id}`}>
                            {row.ref}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge variant="outline" className={`${STATUS_TONE[row.status]} text-[10px] capitalize`}>{row.status}</Badge>
                        {row.draft && (
                          <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-700 bg-amber-500/10"
                                 data-testid={`rx-draft-${row.id}`}>
                            draft
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="btn-pill h-8"
                        onClick={() => setOpen(row.id)}
                        data-testid={`rx-open-${row.id}`}
                      >
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {open && (
          <RxUploadDetail
            id={open}
            statuses={data.statuses || []}
            onClose={() => setOpen(null)}
            onSaved={load}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default RxUploadsTab;
